import React, { useState, useRef, useEffect } from 'react';
import { SlidersHorizontal, Check, X } from 'lucide-react';
import { useMediaQuery, useIsPhone } from './shared/useMediaQuery';
import '../styles/multi-select-filter.css';

// Ancho mínimo del desplegable (igual que .filter-menu-dropdown en el CSS).
const DROPDOWN_MIN_WIDTH = 220;
const VIEWPORT_MARGIN = 8;

// Botón "Filtro" único que agrupa varias dimensiones (estados, épicas...)
// en un solo dropdown con secciones. Cada sección es multi-select:
// [] = todos. Muestra badge con el total de opciones activas.
//
// Responsive (oct-2026): hasta 768 px el botón vive dentro de una tira de
// filtros con desplazamiento horizontal, que recortaría un desplegable
// absoluto. Por eso en compacto el desplegable es fixed, anclado al botón y
// acotado al viewport (patrón 5), y en teléfono pasa a hoja inferior (.u-sheet).
// En escritorio se queda como popover absoluto, igual que antes.
function FilterMenu({ sections }) {
  const [isOpen, setIsOpen] = useState(false);
  const [fixedPos, setFixedPos] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const isCompact = useMediaQuery('(max-width: 768px)');
  const isPhone = useIsPhone();

  // Coordenadas del popover fijo (compacto, no teléfono) acotadas al viewport.
  const measure = () => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.max(DROPDOWN_MIN_WIDTH, rect.width);
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN));
    const top = rect.bottom + 4;
    return { left, top, maxHeight: Math.max(160, window.innerHeight - top - VIEWPORT_MARGIN) };
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    // Popover fijo (compacto, no teléfono): sigue al botón si la página o la
    // tira de filtros se desplazan. La hoja de teléfono no depende del botón.
    const handleScroll = (e) => {
      if (e.target instanceof Element && e.target.closest('.filter-menu-dropdown')) return;
      setFixedPos(measure());
    };
    const handleResize = () => setIsOpen(false);
    const followsTrigger = isCompact && !isPhone;

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      if (followsTrigger) {
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
      }
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, isCompact, isPhone]);

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    setFixedPos(isCompact && !isPhone ? measure() : null);
    setIsOpen(true);
  };

  const activeCount = sections.reduce((sum, s) => sum + s.selected.length, 0);

  const handleToggleOption = (section, value) => {
    if (section.selected.includes(value)) {
      section.onChange(section.selected.filter(v => v !== value));
    } else {
      section.onChange([...section.selected, value]);
    }
  };

  const dropdownClass = [
    'multi-select-dropdown',
    'filter-menu-dropdown',
    isPhone ? 'u-sheet' : '',
    !isPhone && isCompact && fixedPos ? 'is-fixed' : '',
  ].filter(Boolean).join(' ');

  const dropdownStyle = !isPhone && isCompact && fixedPos
    ? { left: fixedPos.left, top: fixedPos.top, maxHeight: Math.min(360, fixedPos.maxHeight) }
    : undefined;

  return (
    <div className="multi-select-filter filter-menu" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`multi-select-trigger ${activeCount > 0 ? 'has-selection' : ''} ${isOpen ? 'is-open' : ''}`}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <SlidersHorizontal size={14} className="multi-select-icon" />
        <span className="multi-select-text">Filtro</span>
        {activeCount > 0 && (
          <span className="multi-select-count">{activeCount}</span>
        )}
      </button>

      {isOpen && isPhone && (
        <div className="filter-menu-scrim" aria-hidden="true" onClick={() => setIsOpen(false)} />
      )}

      {isOpen && (
        <div
          className={dropdownClass}
          style={dropdownStyle}
          role={isPhone ? 'dialog' : undefined}
          aria-modal={isPhone ? 'true' : undefined}
          aria-label={isPhone ? 'Filtros' : undefined}
        >
          {isPhone && (
            <div className="filter-menu-sheet-head">
              <span className="filter-menu-sheet-title">Filtros</span>
              <button
                type="button"
                className="filter-menu-sheet-close"
                onClick={() => setIsOpen(false)}
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            </div>
          )}
          {sections.map((section, i) => (
            <div key={section.key}>
              {i > 0 && <div className="multi-select-divider" />}
              <div className="filter-menu-section-title">{section.title}</div>
              {section.options.map(option => {
                const isSelected = section.selected.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`multi-select-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleToggleOption(section, option.value)}
                  >
                    <span className={`multi-select-checkbox ${isSelected ? 'checked' : ''}`}>
                      {isSelected && <Check size={12} />}
                    </span>
                    <span className="multi-select-label">{option.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FilterMenu;
